// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
import { z } from "zod";

export const openProjectTimesheetVersionSchema = z.object({
  id: z.number(),
  uid: z.string().uuid(),
  legacyOpenProjectId: z.number().nullable(),
  month: z.string(),
  versionNumber: z.number(),
  status: z.string(),
  currentVersion: z.boolean(),
  generatedAt: z.string(),
  generatedByName: z.string(),
  generationReason: z.string().default(""),
  sourceChecksum: z.string(),
  pdfChecksum: z.string(),
  pdfProvenance: z.enum([
    "tapp_generated",
    "legacy_original",
    "regenerated_missing_legacy_pdf",
  ]),
  legacyExpectedPdfChecksum: z.string(),
  releasedAt: z.string().nullable(),
  releaseReason: z.string(),
});

export const openProjectTimesheetVersionStateSchema = z.object({
  month: z.string(),
  locked: z.boolean(),
  currentVersion: openProjectTimesheetVersionSchema.nullable(),
  versions: z.array(openProjectTimesheetVersionSchema),
});

export const openProjectPohodaPreviewEmployeeSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string(),
  ready: z.boolean(),
  status: z.string(),
  statusLabel: z.string(),
  versionId: z.number().nullable(),
  versionNumber: z.number().nullable(),
  absenceEntryCount: z.number(),
  totalHours: z.number(),
  absenceHours: z.number(),
  vacationHours: z.number(),
  doctorHours: z.number(),
  illnessHours: z.number(),
  familyCareHours: z.number(),
  publicHolidayHours: z.number(),
  paidLeaveHours: z.number(),
  valueCorrections: z.array(
    z.object({
      id: z.number().int().positive(),
      fieldKey: z.enum([
        "timesheet.total",
        "vacation.timesheet",
        "doctor.timesheet",
        "family_care.timesheet",
        "illness.timesheet",
        "public_holiday.timesheet",
        "paid_leave.timesheet",
      ]),
      revision: z.number().int().positive(),
      effectiveValueHours: z.number().nullable(),
    }),
  ),
});

export const openProjectPohodaPreviewSchema = z.object({
  month: z.string(),
  policy: z.literal("pdf_finalized"),
  employeeCount: z.number(),
  readyCount: z.number(),
  blockedCount: z.number(),
  warningCount: z.number(),
  dataQuality: z
    .object({
      missingSubjectEntryCount: z.number().int().nonnegative(),
      missingSubjectHours: z.number().finite(),
      unclassifiedSubjectEntryCount: z.number().int().nonnegative(),
      unclassifiedSubjectHours: z.number().finite(),
      sourceChecksum: z.string().regex(/^[a-f0-9]{64}$/),
      disposition: z.enum(["clean", "review_required"]),
    })
    .optional(),
  employees: z.array(openProjectPohodaPreviewEmployeeSchema),
});

export const openProjectPohodaExportVersionSummarySchema = z.object({
  id: z.number(),
  month: z.string(),
  versionNumber: z.number().int().positive(),
  fileName: z.string(),
  generatedByUserId: z.number().nullable(),
  generatedByName: z.string(),
  generatedAt: z.string(),
  includedEmployeeCount: z.number().int().nonnegative(),
  warningCount: z.number().int().nonnegative(),
  sourceMetadata: z.record(z.unknown()),
  sourceChecksum: z.string(),
  xlsxChecksum: z.string(),
  artifactChecksum: z.string(),
  downloadUrl: z.string(),
});

export const openProjectPohodaExportHistorySchema = z.object({
  month: z.string(),
  exports: z.array(openProjectPohodaExportVersionSummarySchema),
});

export const openProjectPayslipEmployeeSchema = z.object({
  id: z.number(),
  displayName: z.string(),
  email: z.string(),
  payrollEmployeeCode: z.string().default(""),
});

export const openProjectPayslipStructuredFieldsSchema = z.object({
  payrollCode: z.string().default(""),
  employeeName: z.string().default(""),
  period: z.string().default(""),
  employmentType: z.string().default(""),
  nominalWorkDays: z.number().nullable().default(null),
  nominalHolidayDays: z.number().nullable().default(null),
  nominalHours: z.number().nullable().default(null),
  paidDays: z.number().nullable().default(null),
  paidHours: z.number().nullable().default(null),
  sickCareParentalDays: z.number().nullable().default(null),
  illnessDays: z.number().nullable().default(null),
  familyCareDays: z.number().nullable().default(null),
  parentalLeaveDays: z.number().nullable().default(null),
  remainingVacationDays: z.number().nullable().default(null),
  vacationDays: z.number().nullable().default(null),
  vacationHours: z.number().nullable().default(null),
  publicHolidayDays: z.number().nullable().default(null),
  publicHolidayHours: z.number().nullable().default(null),
  holidayDays: z.number().nullable().default(null),
  paidLeaveDays: z.number().nullable().default(null),
  paidLeaveHours: z.number().nullable().default(null),
  weekendHours: z.number().nullable().default(null),
  nightHours: z.number().nullable().default(null),
  sundayHours: z.number().nullable().default(null),
  grossPay: z.number().nullable().default(null),
  netPay: z.number().nullable().default(null),
});

export const openProjectPayslipUploadFileResultSchema = z.object({
  currentStatus: z.enum(["ignored", "replaced"]).optional(),
  fileName: z.string(),
  fileSize: z.number().int().nonnegative(),
  checksum: z.string(),
  outcome: z.enum(["accepted", "review", "rejected"]),
  code: z.string(),
  message: z.string(),
  recordId: z.number().int().positive().optional(),
  employeeId: z.number().int().positive().optional(),
  employeeName: z.string().optional(),
});

export const openProjectPayslipUploadSummarySchema = z.object({
  batchId: z.number().int().positive(),
  acceptedCount: z.number().int().nonnegative(),
  reviewCount: z.number().int().nonnegative(),
  rejectedCount: z.number().int().nonnegative(),
  files: z.array(openProjectPayslipUploadFileResultSchema),
});

export const openProjectPayslipUploadResponseSchema = z.object({
  uploadSummary: openProjectPayslipUploadSummarySchema,
});

export const openProjectPayslipBatchSchema = z.object({
  id: z.number(),
  month: z.string(),
  uploadedByUserId: z.number().nullable(),
  uploadedByName: z.string(),
  uploadedAt: z.string(),
  fileCount: z.number(),
  recordCount: z.number(),
  status: z.string(),
  errorSummary: z.string().default(""),
  fileResults: z.array(openProjectPayslipUploadFileResultSchema),
});

export const openProjectPayslipRecordSchema = z.object({
  id: z.number(),
  batchId: z.number(),
  month: z.string(),
  employee: openProjectPayslipEmployeeSchema.nullable(),
  parserStatus: z.string(),
  matchStatus: z.string(),
  parsedPayrollCode: z.string().default(""),
  parsedEmployeeName: z.string().default(""),
  parsedPeriod: z.string().default(""),
  confirmedPeriod: z.string().default(""),
  parsedFields: z.record(z.unknown()).default({}),
  sourceFileName: z.string(),
  sourceFileSize: z.number(),
  pdfFileName: z.string(),
  pageStart: z.number().nullable(),
  pageEnd: z.number().nullable(),
  sectionIndex: z.number().nullable(),
  sectionCount: z.number().nullable(),
  splitStrategy: z.string().default(""),
  splitConfidence: z.string().default(""),
  cropBox: z.record(z.unknown()).default({}),
  parserWarnings: z.array(z.string()),
  validationCodes: z.array(z.string()),
  singleEmployeeVerified: z.boolean(),
  deliverySafe: z.boolean(),
  resolvable: z.boolean(),
  requiresReplacement: z.boolean(),
  duplicateOfRecordId: z.number().nullable(),
  replacedByRecordId: z.number().nullable(),
  manualResolvedByUserId: z.number().nullable(),
  manualResolvedAt: z.string().nullable(),
  manualResolutionReason: z.string().default(""),
  errorMessage: z.string().default(""),
  usable: z.boolean(),
  warnings: z.array(z.string()),
  structuredFields: openProjectPayslipStructuredFieldsSchema
    .partial()
    .default({}),
  calculationReady: z.boolean().default(false),
  downloadUrl: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const openProjectPayslipListSchema = z.object({
  month: z.string(),
  batches: z.array(openProjectPayslipBatchSchema),
  records: z.array(openProjectPayslipRecordSchema),
});

export const openProjectBalanceAdjustmentTypeSchema = z.enum([
  "overtime_hours",
  "holiday_days",
]);
export const openProjectBalanceAdjustmentInputUnitSchema = z.enum([
  "hours",
  "days",
]);

export const openProjectBalanceAdjustmentPreviewRequestSchema = z
  .object({
    userId: z.number().int().positive(),
    balanceType: openProjectBalanceAdjustmentTypeSchema,
    inputAmount: z.coerce.number().finite(),
    inputUnit: openProjectBalanceAdjustmentInputUnitSchema,
    effectiveMonth: z.string().min(7),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.balanceType === "overtime_hours" && value.inputUnit !== "hours") {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["inputUnit"],
        message: "Overtime adjustments must be entered in hours.",
      });
    }
  });

export const openProjectBalanceAdjustmentSchema = z
  .object({
    requestId: z
      .string()
      .trim()
      .min(8)
      .max(100)
      .regex(
        /^[A-Za-z0-9][A-Za-z0-9._:-]{7,99}$/,
        "Use 8-100 URL-safe characters.",
      ),
    userId: z.number().int().positive(),
    balanceType: openProjectBalanceAdjustmentTypeSchema,
    adjustmentType: z
      .enum(["opening", "manual", "entitlement", "conversion"])
      .default("manual"),
    inputAmount: z.coerce.number().finite(),
    inputUnit: openProjectBalanceAdjustmentInputUnitSchema,
    effectiveMonth: z.string().min(7),
    expectedRevision: z.number().int().nonnegative(),
    conversionBasis: z.string().trim().min(1).max(128),
    reason: z.string().trim().min(1).max(500),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.balanceType === "overtime_hours" && value.inputUnit !== "hours") {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["inputUnit"],
        message: "Overtime adjustments must be entered in hours.",
      });
    }
  });

export const openProjectBalanceAdjustmentPreviewResponseSchema = z.object({
  balanceType: openProjectBalanceAdjustmentTypeSchema,
  inputAmount: z.number(),
  inputUnit: openProjectBalanceAdjustmentInputUnitSchema,
  canonicalAmount: z.number(),
  canonicalUnit: openProjectBalanceAdjustmentInputUnitSchema,
  hoursPerDay: z.number().positive(),
  effectiveDeltaHours: z.number(),
  effectiveDeltaDays: z.number().nullable(),
  beforeEffectiveBalanceHours: z.number().nullable(),
  beforeEffectiveBalanceDays: z.number().nullable(),
  afterEffectiveBalanceHours: z.number().nullable(),
  afterEffectiveBalanceDays: z.number().nullable(),
  revision: z.number().int().nonnegative(),
  conversionBasis: z.string().min(1),
});

export const openProjectBalanceExceptionResolutionActionSchema = z.enum([
  "confirm_overtime",
  "accept_exception",
]);

export const openProjectPayrollValueCorrectionFieldKeySchema = z.enum([
  "timesheet.total",
  "payslip.paid",
  "overtime.calculated",
  "vacation.timesheet",
  "vacation.payslip",
  "vacation_remaining.calculated",
  "vacation_remaining.payslip",
  "doctor.timesheet",
  "doctor.payslip",
  "family_care.timesheet",
  "family_care.payslip",
  "illness.timesheet",
  "illness.payslip",
  "public_holiday.timesheet",
  "public_holiday.payslip",
  "paid_leave.timesheet",
  "paid_leave.payslip",
]);

export const openProjectPayrollValueCorrectionReasonCategorySchema = z.enum([
  "source_error",
  "payroll_document_error",
  "calculation_error",
  "approved_exception",
  "rounding",
  "other",
]);

export const OPENPROJECT_PAYROLL_EXCEPTIONAL_CORRECTION_WARNING_VERSION =
  "closed-payroll-correction-v1" as const;

export const openProjectPayrollValueCorrectionMutationSchema = z
  .object({
    requestId: z
      .string()
      .trim()
      .min(8)
      .max(100)
      .regex(
        /^[A-Za-z0-9][A-Za-z0-9._:-]{7,99}$/,
        "Use 8-100 URL-safe characters.",
      ),
    operation: z.enum(["set", "revert", "reaffirm"]),
    valueHours: z.number().finite().optional(),
    expectedRevision: z.number().int().nonnegative(),
    sourceFingerprint: z.string().trim().min(1).max(128),
    reasonCategory: openProjectPayrollValueCorrectionReasonCategorySchema,
    note: z.string().trim().min(3).max(500),
    exceptionalAccess: z
      .object({
        acknowledged: z.literal(true),
        warningVersion: z.literal(
          OPENPROJECT_PAYROLL_EXCEPTIONAL_CORRECTION_WARNING_VERSION,
        ),
      })
      .strict()
      .optional(),
  })
  .superRefine((value, context) => {
    if (value.operation === "set" && value.valueHours === undefined)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["valueHours"],
        message: "Set requires a value in hours.",
      });
    if (value.operation !== "set" && value.valueHours !== undefined)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["valueHours"],
        message: "Revert and reaffirm must omit valueHours.",
      });
  });

export const openProjectPayrollValueCorrectionFieldMetadataSchema = z.object({
  key: openProjectPayrollValueCorrectionFieldKeySchema,
  label: z.string(),
  unit: z.literal("hours"),
  source: z.enum(["timesheet", "payslip", "calculated"]),
  comparisonKey: z.string().nullable(),
  comparisonSide: z.enum(["timesheetValue", "payslipValue"]).nullable(),
  invalidationStage: z.enum([
    "prepare",
    "pohoda",
    "payslips",
    "reconcile",
    "close",
  ]),
  propagation: z.enum([
    "none",
    "future",
    "calendar_year",
    "calendar_year_if_january",
  ]),
  minimumHours: z.number(),
  maximumHours: z.number(),
  decimalPlaces: z.literal(2),
});

export const openProjectPayrollValueCorrectionActiveSchema = z.object({
  revision: z.number().int().positive(),
  valueHours: z.number().nullable(),
  reviewedSourceFingerprint: z.string().nullable().optional(),
  reasonCategory: openProjectPayrollValueCorrectionReasonCategorySchema,
  note: z.string(),
  exceptionalAccess: z.boolean().optional(),
  exceptionalWarningVersion: z.string().optional(),
  exceptionalBlockingMonths: z.array(z.string()).optional(),
  actorEmployeeId: z.number().int().positive().nullable().optional(),
  actorName: z.string(),
  occurredAt: z.string(),
});

export const openProjectPayrollValueCorrectionEventSchema = z.object({
  id: z.number().int().positive(),
  requestId: z.string(),
  employeeId: z.number().int().positive(),
  month: z.string(),
  fieldKey: openProjectPayrollValueCorrectionFieldKeySchema,
  revision: z.number().int().positive(),
  operation: z.enum(["set", "revert", "reaffirm"]),
  valueHours: z.number().nullable(),
  originalValueHours: z.number().nullable(),
  beforeValueHours: z.number().nullable(),
  afterValueHours: z.number().nullable(),
  sourceFingerprint: z.string(),
  reasonCategory: openProjectPayrollValueCorrectionReasonCategorySchema,
  note: z.string(),
  exceptionalAccess: z.boolean().optional(),
  exceptionalWarningVersion: z.string().optional(),
  exceptionalBlockingMonths: z.array(z.string()).optional(),
  actorEmployeeId: z.number().int().positive().nullable(),
  actorName: z.string(),
  occurredAt: z.string(),
});

export const openProjectPayrollValueCorrectionFieldStateSchema = z.object({
  fieldKey: openProjectPayrollValueCorrectionFieldKeySchema,
  originalValueHours: z.number().nullable(),
  effectiveValueHours: z.number().nullable(),
  corrected: z.boolean(),
  revision: z.number().int().nonnegative(),
  sourceFingerprint: z.string(),
  reviewedSourceFingerprint: z.string().nullable(),
  reviewRequired: z.boolean(),
  sourceReviewState: z.enum(["not_corrected", "reviewed", "review_required"]),
  activeCorrection: openProjectPayrollValueCorrectionActiveSchema.nullable(),
});

export const openProjectPayrollValueCorrectionMetadataSchema = z.object({
  hasCorrections: z.boolean().default(false),
  reviewRequired: z.boolean().default(false),
  correctedFieldKeys: z
    .array(openProjectPayrollValueCorrectionFieldKeySchema)
    .default([]),
  effectiveValues: z
    .record(
      openProjectPayrollValueCorrectionFieldKeySchema,
      z.number().nullable(),
    )
    .default({}),
  fields: z
    .record(
      openProjectPayrollValueCorrectionFieldKeySchema,
      openProjectPayrollValueCorrectionFieldStateSchema,
    )
    .default({}),
});

export const openProjectPayrollValueCorrectionInspectionSchema = z.object({
  employeeId: z.number().int().positive(),
  employeeName: z.string(),
  month: z.string(),
  field: openProjectPayrollValueCorrectionFieldMetadataSchema,
  allowedReasonCategories: z.array(
    openProjectPayrollValueCorrectionReasonCategorySchema,
  ),
  originalValueHours: z.number().nullable(),
  effectiveValueHours: z.number().nullable(),
  activeCorrection: openProjectPayrollValueCorrectionActiveSchema.nullable(),
  revision: z.number().int().nonnegative(),
  sourceFingerprint: z.string(),
  sourceReview: z.object({
    state: z.enum(["not_corrected", "reviewed", "review_required"]),
    reviewRequired: z.boolean(),
    reviewedSourceFingerprint: z.string().nullable(),
    currentSourceFingerprint: z.string(),
  }),
  editEligibility: z.object({
    editable: z.boolean(),
    canMutate: z.boolean(),
    blockingMonths: z.array(z.string()),
    code: z
      .enum([
        "payroll_correction_month_closed",
        "payroll_correction_carry_forward_closed",
      ])
      .nullable(),
    exceptionalAccess: z.object({
      authorized: z.boolean(),
      available: z.boolean(),
      required: z.boolean(),
      warningVersion: z.literal(
        OPENPROJECT_PAYROLL_EXCEPTIONAL_CORRECTION_WARNING_VERSION,
      ),
    }),
  }),
  events: z.array(openProjectPayrollValueCorrectionEventSchema),
});

const openProjectPayrollValueCorrectionRecalculationIssueSchema = z.object({
  month: z.string(),
  code: z.string(),
  message: z.string(),
});

export const openProjectPayrollValueCorrectionMutationResponseSchema = z.object(
  {
    mutation: z.object({
      event: openProjectPayrollValueCorrectionEventSchema,
      affectedMonths: z.array(z.string()),
      replayed: z.boolean(),
    }),
    inspection: openProjectPayrollValueCorrectionInspectionSchema,
    recalculation: z.object({
      state: z.enum(["complete", "pending", "failed"]),
      affectedMonths: z.array(z.string()),
      recalculatedMonths: z.array(z.string()),
      pendingMonths: z.array(
        openProjectPayrollValueCorrectionRecalculationIssueSchema,
      ),
      failedMonths: z.array(
        openProjectPayrollValueCorrectionRecalculationIssueSchema,
      ),
    }),
  },
);

export const openProjectPayrollAuditLevelSchema = z.enum([
  "business",
  "changes",
  "technical",
]);
export const openProjectPayrollAuditEventKindSchema = z.enum([
  "source_control",
  "source_import",
  "profile_change",
  "payroll_export",
  "payslip_intake",
  "payslip_resolution",
  "value_correction",
  "exceptional_value_correction",
  "calculation",
  "balance_adjustment",
  "exception_resolution",
  "approval",
  "workflow",
  "month_control",
  "invalidation",
  "unclassified",
]);
export const openProjectPayrollAuditScopeSchema = z.enum([
  "employee",
  "month",
  "period",
  "document",
  "workflow",
  "system",
]);
const openProjectPayrollAuditMonthSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/);
const openProjectPayrollAuditFilterObjectSchema = z
  .object({
    level: openProjectPayrollAuditLevelSchema.default("business"),
    userId: z.number().int().positive().optional(),
    month: openProjectPayrollAuditMonthSchema.optional(),
    fromMonth: openProjectPayrollAuditMonthSchema.optional(),
    throughMonth: openProjectPayrollAuditMonthSchema.optional(),
    fieldKey: openProjectPayrollValueCorrectionFieldKeySchema.optional(),
    eventKind: openProjectPayrollAuditEventKindSchema.optional(),
    scope: openProjectPayrollAuditScopeSchema.optional(),
  })
  .strict();
export const openProjectPayrollAuditFilterSchema =
  openProjectPayrollAuditFilterObjectSchema.superRefine((value, context) => {
    if (value.month && (value.fromMonth || value.throughMonth))
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["month"],
        message: "Use either an exact month or a period range.",
      });
    if (
      value.fromMonth &&
      value.throughMonth &&
      value.fromMonth > value.throughMonth
    )
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["throughMonth"],
        message: "Period end must not precede period start.",
      });
    if (value.fieldKey && (!value.userId || !value.month))
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["fieldKey"],
        message:
          "Field audit filters require both an employee and an exact month.",
      });
  });

export const openProjectPayrollAuditEventSchema = z.object({
  id: z.number().int().positive(),
  actorDisplayName: z.string(),
  action: z.string(),
  reason: z.string(),
  classification: z.object({
    kind: openProjectPayrollAuditEventKindSchema,
    scope: openProjectPayrollAuditScopeSchema,
    manualChange: z.boolean(),
    exceptionalAccess: z.boolean(),
  }),
  context: z.record(z.unknown()),
  createdAt: z.string(),
  targetType: z.string().optional(),
  targetIdentifier: z.string().optional(),
  beforePayload: z.record(z.unknown()).optional(),
  afterPayload: z.record(z.unknown()).optional(),
});

export const openProjectPayrollAuditResponseSchema = z.object({
  level: openProjectPayrollAuditLevelSchema,
  filters: openProjectPayrollAuditFilterObjectSchema
    .omit({ level: true })
    .partial(),
  events: z.array(openProjectPayrollAuditEventSchema),
});

export const openProjectCrossCheckSchema = z.object({
  state: z.enum(["green", "yellow", "red"]).default("green"),
  message: z.string().default(""),
  timesheetValue: z.number().nullable().default(null),
  payslipValue: z.number().nullable().default(null),
  difference: z.number().nullable().default(null),
  rawDifference: z.number().nullable().optional(),
  appliedAdjustment: z.number().optional(),
  potChange: z.number().optional(),
  accepted: z.boolean().optional(),
  acceptedAt: z.string().optional(),
  acceptedBy: z.string().optional(),
  acceptanceReason: z.string().optional(),
  resolutionAction:
    openProjectBalanceExceptionResolutionActionSchema.optional(),
});

export const openProjectCrossChecksSchema = z
  .object({
    workload: openProjectCrossCheckSchema.optional(),
    overtime: openProjectCrossCheckSchema.optional(),
    vacation: openProjectCrossCheckSchema.optional(),
    doctor: openProjectCrossCheckSchema.optional(),
    familyCare: openProjectCrossCheckSchema.optional(),
    illness: openProjectCrossCheckSchema.optional(),
    publicHoliday: openProjectCrossCheckSchema.optional(),
    paidLeave: openProjectCrossCheckSchema.optional(),
    remainingVacation: openProjectCrossCheckSchema.optional(),
  })
  .catchall(openProjectCrossCheckSchema);

export const openProjectBalanceResultSchema = z.object({
  id: z.number(),
  runId: z.number(),
  userId: z.number(),
  profileId: z.string(),
  employeeName: z.string(),
  payrollEmployeeCode: z.string().default(""),
  payrollContractType: z.string().default("unknown"),
  payrollWorkloadPercent: z.number().default(100),
  hoursPerDay: z.number().positive().default(8),
  month: z.string(),
  statusColor: z.enum(["green", "yellow", "red"]),
  status: z.string(),
  recalculationRequired: z.boolean().default(false),
  recalculationReason: z.string().default(""),
  approved: z.boolean(),
  approvedAt: z.string().nullable(),
  timesheetVersionId: z.number().nullable(),
  timesheetVersionNumber: z.number().nullable(),
  payslipRecordId: z.number().nullable(),
  payslipFileName: z.string().default(""),
  payslipAvailable: z.boolean().default(false),
  payslipVerificationState: z.string().default("unverified"),
  timesheetHours: z.number(),
  payslipPaidHours: z.number().nullable(),
  payslipNominalHours: z.number().nullable().default(null),
  payslipComparableHours: z.number().nullable(),
  payslipHoursMultiplier: z.number(),
  studentAgreementAdjustment: z.boolean(),
  overtimeDeltaHours: z.number(),
  openingOvertimeHours: z.number(),
  manualOvertimeAdjustmentHours: z.number(),
  conversionOvertimeHours: z.number(),
  closingOvertimeHours: z.number(),
  vacationHours: z.number().default(0),
  vacationTakenDays: z.number(),
  vacationBalanceBaselineMonth: z.string().nullable(),
  vacationBalanceBaselineDays: z.number().nullable(),
  vacationBalanceBaselinePayslipRecordId: z.number().nullable(),
  calculatedRemainingVacationDays: z.number().nullable(),
  calculatedRemainingVacationHours: z.number().nullable(),
  payslipRemainingVacationDays: z.number().nullable(),
  payslipRemainingVacationHours: z.number().nullable(),
  remainingVacationDifferenceHours: z.number().nullable(),
  doctorHours: z.number(),
  familyCareHours: z.number().default(0),
  illnessHours: z.number().default(0),
  publicHolidayHours: z.number().default(0),
  paidLeaveHours: z.number().default(0),
  payslipVacationDays: z.number().nullable(),
  vacationMismatchDays: z.number().nullable(),
  openingHolidayDays: z.number(),
  annualEntitlementDays: z.number(),
  accruedHolidayDays: z.number(),
  manualHolidayAdjustmentDays: z.number(),
  conversionHolidayDays: z.number(),
  holidayLeftDays: z.number(),
  closingHolidayDays: z.number(),
  calculatedValues: z.record(z.unknown()).default({}),
  extractedValues: z.record(z.unknown()).default({}),
  effectiveValues: z
    .record(
      openProjectPayrollValueCorrectionFieldKeySchema,
      z.number().nullable(),
    )
    .default({}),
  correctionMetadata: openProjectPayrollValueCorrectionMetadataSchema.default(
    {},
  ),
  crossChecks: openProjectCrossChecksSchema.default({}),
  warnings: z.array(z.string()),
  errors: z.array(z.string()),
  inputSnapshot: z.record(z.unknown()).default({}),
});

export const openProjectBalanceRunSchema = z.object({
  id: z.number(),
  month: z.string(),
  status: z.string(),
  triggerType: z.string(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  employeeCount: z.number(),
  greenCount: z.number(),
  yellowCount: z.number(),
  redCount: z.number(),
  sourceChecksum: z.string(),
  notes: z.string().default(""),
  results: z.array(openProjectBalanceResultSchema),
});

export const openProjectCalculationReadinessMissingSchema = z.enum([
  "no_eligible_employees",
  "finalized_timesheet",
  "usable_payslip",
  "delivery_safe_payslip",
  "confirmed_payslip",
]);

export const openProjectCalculationReadinessSchema = z.object({
  ready: z.boolean(),
  eligibleCount: z.number().int().nonnegative(),
  completeCount: z.number().int().nonnegative(),
  blockers: z.array(
    z.object({
      employeeId: z.number().int().positive().nullable(),
      employeeName: z.string(),
      missing: z.array(openProjectCalculationReadinessMissingSchema),
    }),
  ),
});

export const openProjectBalanceStateSchema = z.object({
  month: z.string(),
  readiness: openProjectCalculationReadinessSchema,
  latestRun: openProjectBalanceRunSchema.nullable(),
});

export const openProjectPayrollAccessSchema = z.object({
  user: z.object({
    id: z.number(),
    login: z.string(),
    name: z.string(),
    email: z.string(),
  }),
  officeAccess: z.boolean(),
  studentStatus: z.boolean(),
  profile: z.record(z.unknown()),
  groups: z
    .object({
      office: z.string(),
      students: z.string(),
    })
    .optional(),
  legacyGroups: z
    .object({
      office: z.string(),
      students: z.string(),
    })
    .optional(),
});

export const openProjectPeriodSummaryMonthSchema = z.object({
  month: z.string(),
  hasResult: z.boolean(),
  approved: z.boolean(),
  timesheetVersionNumber: z.number().nullable(),
  timesheetHours: z.number().nullable(),
  payslipPaidHours: z.number().nullable(),
  payslipNominalHours: z.number().nullable().default(null),
  payslipComparableHours: z.number().nullable(),
  payslipHoursMultiplier: z.number(),
  studentAgreementAdjustment: z.boolean(),
  payrollContractType: z.string().optional(),
  payrollWorkloadPercent: z.number().optional(),
  hoursPerDay: z.number().nullable(),
  overtimeDeltaHours: z.number().nullable(),
  closingOvertimeHours: z.number().nullable(),
  vacationHours: z.number().nullable(),
  vacationTakenDays: z.number().nullable(),
  payslipVacationDays: z.number().nullable(),
  vacationMismatchDays: z.number().nullable(),
  vacationBalanceBaselineMonth: z.string().nullable(),
  vacationBalanceBaselineDays: z.number().nullable(),
  vacationBalanceBaselinePayslipRecordId: z.number().nullable(),
  calculatedRemainingVacationDays: z.number().nullable(),
  calculatedRemainingVacationHours: z.number().nullable(),
  payslipRemainingVacationDays: z.number().nullable(),
  payslipRemainingVacationHours: z.number().nullable(),
  remainingVacationDifferenceHours: z.number().nullable(),
  doctorHours: z.number().nullable(),
  familyCareHours: z.number().nullable().default(null),
  illnessHours: z.number().nullable(),
  publicHolidayHours: z.number().nullable().default(null),
  paidLeaveHours: z.number().nullable().default(null),
  calculatedValues: z.record(z.unknown()).default({}),
  extractedValues: z.record(z.unknown()).default({}),
  effectiveValues: z
    .record(
      openProjectPayrollValueCorrectionFieldKeySchema,
      z.number().nullable(),
    )
    .default({}),
  correctionMetadata: openProjectPayrollValueCorrectionMetadataSchema.default(
    {},
  ),
  crossChecks: openProjectCrossChecksSchema.default({}),
  statusColor: z.string().optional(),
  holidayLeftDays: z.number().nullable().optional(),
  warnings: z.array(z.string()).optional(),
  errors: z.array(z.string()).optional(),
});

export const openProjectPeriodSummaryEmployeeSchema = z.object({
  userId: z.number(),
  employeeName: z.string(),
  payrollEmployeeCode: z.string().default(""),
  overtimePotHours: z.number(),
  overtimePotDays: z.number().default(0),
  totalDoctorHours: z.number(),
  totalFamilyCareHours: z.number().default(0),
  totalIllnessHours: z.number().default(0),
  totalPublicHolidayHours: z.number().default(0),
  totalPaidLeaveHours: z.number().default(0),
  totalVacationHours: z.number().default(0),
  vacationTakenDays: z.number(),
  remainingVacationDays: z.number().nullable(),
  remainingVacationHours: z.number().nullable(),
  payslipRemainingVacationDays: z.number().nullable(),
  payslipRemainingVacationHours: z.number().nullable(),
  remainingVacationCrossCheck: openProjectCrossCheckSchema
    .nullable()
    .optional(),
  monthlyRows: z.array(openProjectPeriodSummaryMonthSchema).default([]),
  holidaysLeftDays: z.number().optional(),
  statusColor: z.string().optional(),
  notes: z.string().optional(),
});

export const openProjectYearSummarySchema = z.object({
  year: z.number(),
  throughMonth: z.string(),
  fromMonth: z.string().optional(),
  employees: z.array(openProjectPeriodSummaryEmployeeSchema),
});

export const openProjectPeriodSummarySchema = z.object({
  fromMonth: z.string(),
  throughMonth: z.string(),
  employees: z.array(openProjectPeriodSummaryEmployeeSchema),
});

export type OpenProjectTimesheetVersion = z.infer<
  typeof openProjectTimesheetVersionSchema
>;
export type OpenProjectTimesheetVersionState = z.infer<
  typeof openProjectTimesheetVersionStateSchema
>;
export type OpenProjectPohodaPreview = z.infer<
  typeof openProjectPohodaPreviewSchema
>;
export type OpenProjectPayslipList = z.infer<
  typeof openProjectPayslipListSchema
>;
export type OpenProjectPayslipRecord = z.infer<
  typeof openProjectPayslipRecordSchema
>;
export type OpenProjectBalanceState = z.infer<
  typeof openProjectBalanceStateSchema
>;
export type OpenProjectBalanceAdjustmentPreviewRequest = z.infer<
  typeof openProjectBalanceAdjustmentPreviewRequestSchema
>;
export type OpenProjectBalanceAdjustment = z.infer<
  typeof openProjectBalanceAdjustmentSchema
>;
export type OpenProjectBalanceAdjustmentPreviewResponse = z.infer<
  typeof openProjectBalanceAdjustmentPreviewResponseSchema
>;
export type OpenProjectPayrollAccess = z.infer<
  typeof openProjectPayrollAccessSchema
>;
export type OpenProjectYearSummary = z.infer<
  typeof openProjectYearSummarySchema
>;
export type OpenProjectPeriodSummary = z.infer<
  typeof openProjectPeriodSummarySchema
>;
export type OpenProjectPayrollValueCorrectionFieldKey = z.infer<
  typeof openProjectPayrollValueCorrectionFieldKeySchema
>;
export type OpenProjectPayrollValueCorrectionMutation = z.infer<
  typeof openProjectPayrollValueCorrectionMutationSchema
>;
export type OpenProjectPayrollValueCorrectionInspection = z.infer<
  typeof openProjectPayrollValueCorrectionInspectionSchema
>;
export type OpenProjectPayrollValueCorrectionMutationResponse = z.infer<
  typeof openProjectPayrollValueCorrectionMutationResponseSchema
>;
export type OpenProjectPayrollAuditLevel = z.infer<
  typeof openProjectPayrollAuditLevelSchema
>;
export type OpenProjectPayrollAuditEventKind = z.infer<
  typeof openProjectPayrollAuditEventKindSchema
>;
export type OpenProjectPayrollAuditScope = z.infer<
  typeof openProjectPayrollAuditScopeSchema
>;
export type OpenProjectPayrollAuditFilter = z.infer<
  typeof openProjectPayrollAuditFilterSchema
>;
export type OpenProjectPayrollAuditEvent = z.infer<
  typeof openProjectPayrollAuditEventSchema
>;
export type OpenProjectPayrollAuditResponse = z.infer<
  typeof openProjectPayrollAuditResponseSchema
>;
export type PohodaExportVersionSummary = z.infer<
  typeof openProjectPohodaExportVersionSummarySchema
>;
export type OpenProjectPohodaExportHistory = z.infer<
  typeof openProjectPohodaExportHistorySchema
>;
export type PayslipUploadFileResult = z.infer<
  typeof openProjectPayslipUploadFileResultSchema
>;
export type PayslipUploadSummary = z.infer<
  typeof openProjectPayslipUploadSummarySchema
>;
export type OpenProjectPayslipUploadResponse = z.infer<
  typeof openProjectPayslipUploadResponseSchema
>;
export type OpenProjectPayslipBatch = z.infer<
  typeof openProjectPayslipBatchSchema
>;
export type CalculationReadinessMissing = z.infer<
  typeof openProjectCalculationReadinessMissingSchema
>;
export type CalculationReadiness = z.infer<
  typeof openProjectCalculationReadinessSchema
>;
